import { useEffect } from 'react';

import { fetchRefreshToken } from '~/services/token/refresh-token';

// Fin data connectors call our API as the user with this token, and it expires 5 minutes after it is issued.
// Refreshing every 5 minutes would race that expiry and leave a window where Intercom holds a dead token,
// so the Messenger refreshes a minute early.
const MESSENGER_TOKEN_REFRESH_MS = 4 * 60 * 1000;

const callIntercom = (...args: unknown[]) => {
  if (typeof window.Intercom === 'function') {
    window.Intercom(...args);
  }
};

/**
 * Keeps the access token Intercom holds fresh while the Messenger is open.
 * Intercom only needs a token while it is shown: it is refreshed and sent when the Messenger opens, then every
 * 4 minutes until it is hidden. `enabled` should be false whenever there is no identified Messenger session.
 */
export function useIntercomAuthTokenSync(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let timer: ReturnType<typeof setInterval> | undefined;
    let showGeneration = 0;
    let isDisposed = false;

    const stopRefreshing = () => {
      showGeneration += 1;
      clearInterval(timer);
      timer = undefined;
    };

    const refreshAndSend = async () => {
      const startedInGeneration = showGeneration;

      try {
        const { accessToken } = await fetchRefreshToken(false);

        // A refresh can resolve after the Messenger was hidden (even if it was shown again since) or the user logged
        // out; only a refresh started during the current show may send, and Intercom gets nothing from any other.
        if (startedInGeneration === showGeneration && accessToken) {
          callIntercom('update', { auth_tokens: { splash_user_token: accessToken } });
        }
      } catch {
        // fetchRefreshToken reports its own failures. This tick is skipped and the next one tries again.
      }
    };

    const startRefreshing = () => {
      if (isDisposed) {
        return;
      }

      // Shown twice without a hide in between must not leave the first timer running.
      stopRefreshing();
      refreshAndSend();
      timer = setInterval(refreshAndSend, MESSENGER_TOKEN_REFRESH_MS);
    };

    callIntercom('onShow', startRefreshing);
    callIntercom('onHide', stopRefreshing);

    return () => {
      isDisposed = true;
      stopRefreshing();
    };
  }, [enabled]);
}
